import React from "react";
import { Row, Col, Typography, Button } from "antd";
import {
  PhoneOutlined,
  CloudSyncOutlined,
  BellOutlined,
  FileTextOutlined,
  DownloadOutlined,
  StarFilled,
} from "@ant-design/icons";
import bannerImg from "../../assets/landing-page/banner/2.png";

const { Title, Paragraph, Text } = Typography;

const SHOWCASE_FEATURES = [
  {
    icon: <CloudSyncOutlined />,
    title: "Automatic Background Call Recording & Duration Sync",
    desc: "Runs silently as a native background telephony service. Every outbound and inbound call duration is accurately timed without rep intervention.",
  },
  {
    icon: <PhoneOutlined />,
    title: "Zero-Touch CRM Call Logging",
    desc: "Hang up the call and your DealVoice CRM is already updated with lead timestamps, call duration metrics, and agent activity logs.",
  },
  {
    icon: <BellOutlined />,
    title: "Real-Time Push Alarms & Follow-Up Deadlines",
    desc: "Instant Android notifications the moment a hot lead is assigned or a scheduled follow-up reminder reaches its deadline.",
  },
  {
    icon: <FileTextOutlined />,
    title: "Instant Customer 360 & Invoices on Mobile",
    desc: "Access full client communication histories, past call notes, deal stages, and share PDF GST invoices with Razorpay links right from your phone.",
  },
];

// Official Google Play Vector Icon
const GooglePlayIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    style={{ display: "block", flexShrink: 0 }}
  >
    <path
      fill="#4285F4"
      d="M3.609 1.814L13.792 12 3.61 22.186c-.367-.378-.61-.945-.61-1.636V3.45c0-.691.243-1.258.61-1.636z"
    />
    <path
      fill="#FBBC04"
      d="M17.323 8.469l-3.531 3.531 3.531 3.531 4.195-2.422c.983-.568.983-1.651 0-2.218l-4.195-2.422z"
    />
    <path
      fill="#34A853"
      d="M3.609 1.814l10.183 10.186 3.531-3.531L5.805.908c-.732-.423-1.602-.271-2.196.906z"
    />
    <path
      fill="#EA4335"
      d="M13.792 12L3.61 22.186c.594 1.177 1.464 1.329 2.196.906l11.518-6.64-3.532-3.532z"
    />
  </svg>
);

function MobileShowcaseSection() {
  return (
    <section className="mobile-showcase" id="mobile-app">
      <div className="showcase-container">
        {/* Top Header (Left-Aligned) */}
        <div className="showcase-header-wrap">
          <span className="section-eyebrow-badge">NATIVE ANDROID TELEPHONY</span>
          <Title level={2} className="showcase-main-heading">
            Enterprise sales intelligence{" "}
            <span className="text-highlight-blue">right in your pocket</span>
          </Title>

          {/* Rating (No background, no border) */}
          <div className="showcase-rating-bar">
            <div className="rating-stars">
              {[...Array(5)].map((_, i) => (
                <StarFilled key={i} style={{ color: "#f59e0b", fontSize: "14px" }} />
              ))}
            </div>
            <span className="rating-score">4.9 / 5.0</span>
            <span className="rating-divider">•</span>
            <span className="rating-text">Rated by 1,200+ active field sales executives</span>
          </div>

          <Paragraph className="showcase-main-subheading">
            Designed specifically for on-field sales executives, travelling consultants, and fast-moving closing teams.
          </Paragraph>
        </div>

        <Row gutter={[48, 48]} align="middle" className="showcase-row">
          {/* Left Column: Feature Grid & Store Badges */}
          <Col xs={24} lg={13} className="showcase-content-col">
            {/* Minimalist Feature List */}
            <div className="showcase-features-list">
              {SHOWCASE_FEATURES.map((feat, idx) => (
                <div className="showcase-list-item" key={idx}>
                  <div className="showcase-list-icon">
                    {feat.icon}
                  </div>
                  <div className="showcase-list-text">
                    <Text strong className="showcase-list-title">
                      {feat.title}
                    </Text>
                    <Paragraph className="showcase-list-desc">
                      {feat.desc}
                    </Paragraph>
                  </div>
                </div>
              ))}
            </div>

            {/* Store Download Row */}
            <div className="showcase-store-section">
              <div className="showcase-store-buttons">
                {/* Google Play Button */}
                <a
                  href="https://play.google.com/store/apps/details?id=com.dealvoice.app"
                  target="_blank"
                  rel="noreferrer"
                  className="store-btn-link"
                >
                  <Button className="playstore-btn-custom">
                    <GooglePlayIcon />
                    <div className="store-btn-labels">
                      <span className="store-btn-sub">GET IT ON</span>
                      <span className="store-btn-main">Google Play</span>
                    </div>
                  </Button>
                </a>

                {/* Direct Enterprise APK Button */}
                <a
                  href="/download/dealvoice-enterprise-latest.apk"
                  target="_blank"
                  rel="noreferrer"
                  className="store-btn-link"
                >
                  <Button className="apk-btn-custom">
                    <DownloadOutlined style={{ fontSize: "20px", color: "#10b981" }} />
                    <div className="store-btn-labels">
                      <span className="store-btn-sub">DIRECT DOWNLOAD</span>
                      <span className="store-btn-main">Enterprise APK</span>
                    </div>
                  </Button>
                </a>
              </div>
            </div>
          </Col>

          {/* Right Column: Clean Visual Showcase (Phone Mockup without floating badges) */}
          <Col xs={24} lg={11} className="showcase-visual-col">
            <div className="showcase-visual-wrapper">
              <div className="showcase-glow-backdrop" />
              
              {/* Phone Image Container */}
              <div className="showcase-phone-frame">
                <img
                  src={bannerImg}
                  alt="DealVoice Mobile Telephony App"
                  className="showcase-phone-img"
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
}

export default MobileShowcaseSection;
